<template>
  <div id="app" v-if="ready" style="height:100%">
    <div id="topbar">
      <a id="topbar-brand" href="#/apps" title="apps">
        <b style="font-weight: 800; color: #e8e8e6; font-size: 0.85em; letter-spacing: -0.02em;">turbo</b><span style="font-weight: 300; color: #9ad12b; font-size: 0.85em;">hammer</span>
      </a>
      <span v-if="accountName" id="topbar-app" :title="accountName">{{ accountName }}</span>
      <span v-if="accountRole === 'designer' || accountRole === 'billing'" class="topbar-role">{{ accountRole }}</span>
      <nav id="topbar-nav" aria-label="Account">
        <router-link to="/apps" class="topbar-link" :aria-current="navCurrent('/apps')" :class="{ 'topbar-link-active': $route.path.includes('/apps') }">
          <font-awesome-icon icon="th-large" class="topbar-nav-icon" />apps
        </router-link>
        <router-link v-if="showBilling" to="/account" class="topbar-link" :aria-current="navCurrent('/account')" :class="{ 'topbar-link-active': $route.path === '/account' }">
          <font-awesome-icon icon="user" class="topbar-nav-icon" />account
        </router-link>
        <router-link to="/statistics" class="topbar-link" :aria-current="navCurrent('/statistics')" :class="{ 'topbar-link-active': $route.path === '/statistics' }">
          <font-awesome-icon icon="chart-bar" class="topbar-nav-icon" />statistics
        </router-link>
      </nav>
      <div v-if="otherAccounts.length" class="topbar-switch">
        <label for="account-switch">also building</label>
        <select id="account-switch" @change="openAccount">
          <option value="">{{ accountName || 'this account' }}</option>
          <option v-for="item in otherAccounts" :key="item.name" :value="item.name">{{ item.name }} · {{ item.role }}</option>
        </select>
      </div>
      <div id="topbar-actions">
        <button type="button" class="topbar-action-btn" @click="logout">
          <font-awesome-icon icon="sign-out-alt" class="topbar-nav-icon" />logout
        </button>
      </div>
    </div>
    <div class="content" id="content-container">
      <router-view v-if="ready"></router-view>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useSessionStore } from '../stores/session.js'
import { canOpenBilling } from '../../../shared/account-seat.js'

export default {
  name: 'app',
  data () {

      return {
      msg: '',
      ready: false,
      accountName: '',
      accountRole: 'owner',
      memberships: []
    }
  },
  computed: {
    showBilling () {

      return canOpenBilling(this.accountRole)
    },
    otherAccounts () {

      return this.memberships.filter((item) => item && item.name && item.name !== this.accountName)
    }
  },
  async created () {

    try {

      await axios.get(`${VUE_APP_APP_SERVER_URL}/applications/`, { withCredentials: true })
      await this.loadAccountLabel()
      this.ready = true
    } catch (error) {

      window.location.assign(VUE_APP_ACCOUNT_LOGIN_URL)
    }
  },
  methods: {
    navCurrent (path) {

      const current = path === `/apps`
        ? this.$route.path.includes(`/apps`) || this.$route.path === `/`
        : this.$route.path === path

      return current ? `page` : undefined
    },
    async loadAccountLabel () {

      try {

        const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/account`, { withCredentials: true })

        this.accountName = (response.data && response.data.name) || ``
        this.accountRole = (response.data && response.data.role) || `owner`
        this.memberships = (response.data && response.data.memberships) || []
      } catch (error) {

        this.accountName = ``
      }
    },
    async openAccount (event) {

      const name = event.target.value

      event.target.value = ``

      if (!name) return

      try {

        const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/memberships/open`, { name }, { withCredentials: true })

        if (response.data && response.data.redirectUrl) {

          window.location.assign(response.data.redirectUrl)
        }
      } catch (error) {

        console.log(`could not open that account`)
      }
    },
    async logout () {

      try {

        await axios.post(`${VUE_APP_APP_SERVER_URL}/session/logout`, {}, { withCredentials: true })
      } catch (error) {

        console.log(`error logging out:`)
        console.log(error)
      }

      const sessionStore = useSessionStore()
      sessionStore.destroy()
      window.location.assign(VUE_APP_ACCOUNT_LOGIN_URL)
    }
  },
  components: {
  }
}
</script>

<style>

html, body {
  min-height: 100%;
  height: 100%;
  margin: 0;
}

#app {
  font-family: 'Helvetica Neue', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #e8e8e6;
  background-color: #181916;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* ── Top bar ── */
#topbar {
  display: flex;
  align-items: center;
  height: 40px;
  background-color: #0f0f0d;
  border-bottom: 1px solid #2a2a27;
  padding: 0 12px;
  gap: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
}

#topbar-brand {
  flex-shrink: 0;
  padding-right: 12px;
  border-right: 1px solid #2a2a27;
  text-decoration: none;
}

#topbar-app {
  flex-shrink: 1;
  min-width: 0;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: 600;
  color: #e8e8e6;
}

#topbar-nav {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
}

.topbar-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #707070;
  text-decoration: none !important;
  transition: background 0.12s, color 0.12s;
}
.topbar-link:hover {
  background-color: #1e1e1b;
  color: #b0b0ae;
  text-decoration: none;
}
.topbar-link-active {
  background-color: #1a1a17 !important;
  color: #e8e8e6 !important;
}

.topbar-nav-icon {
  font-size: 0.72rem;
  opacity: 0.6;
}
.topbar-link-active .topbar-nav-icon {
  opacity: 1;
  color: #9ad12b;
}

.topbar-role {
  flex-shrink: 0;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ad12b;
  border: 1px solid #3a4a22;
  border-radius: 999px;
  padding: 2px 7px;
}

.topbar-switch {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 0.72rem;
  color: #707070;
}

.topbar-switch select {
  background: #1a1a17;
  color: #e8e8e6;
  border: 1px solid #2a2a27;
  border-radius: 5px;
  font-size: 0.75rem;
  padding: 3px 6px;
  max-width: 220px;
}

#topbar-actions {
  margin-left: auto;
  flex-shrink: 0;
}

.topbar-action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 5px;
  background: transparent;
  font: inherit;
  font-size: 0.78rem;
  color: #707070;
  cursor: pointer;
  text-decoration: none;
  border: 1px solid #2a2a27;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.topbar-action-btn:hover {
  background-color: #1e1e1b;
  color: #b0b0ae;
  border-color: #3a3a37;
}

/* ── Content area ── */
#content-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

#content-container > * {
  flex: 1 1 auto;
  min-height: 0;
}

a {
  color: #e8e8e6;
}

h1, h2 {
  font-weight: normal;
}

body :focus-visible {
  outline: 2px solid #9ad12b;
  outline-offset: 2px;
}

</style>
